import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Grid2, Card, CardContent, LinearProgress, Box, Select, MenuItem, InputLabel, FormControl, useTheme, useMediaQuery } from '@mui/material';
import { AttachMoney, Category, Person } from '@mui/icons-material';
import { getEventsBudgetCategories } from '../actions/budget';
import { getEventsDetails } from '../actions/events';
import { useLocation } from 'react-router-dom';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import BudgetGuardrail from '../components/BudgetGuardrail';
import PaymentMilestones from '../components/PaymentMilestones';
import { budgetPercentage } from '../utils/budgetPercentage';

const capitalizeName = (name) => {
  if (!name) return '';
  return name
    .split(' ') 
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const MultiViewBudgetTracking = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  
  const eventIds = useMemo(() => {
    const queryParam = new URLSearchParams(location.search).get('eventIds');
    return queryParam ? queryParam.split(',') : [];
  }, [location.search]);

  const [totalBudget, setTotalBudget] = useState(0);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalPaidSoFar, setTotalPaidSoFar] = useState(0);
  
  // New filter states
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedPaidByFilter, setSelectedPaidByFilter] = useState('');

  // Fetch categories and total budget on load
  useEffect(() => {
      const fetchData = async () => {
      try {
        // Multi-event view
        const [fetchedCategories, eventDetails] = await Promise.all([
          getEventsBudgetCategories(eventIds),
          getEventsDetails(eventIds)
        ]);

        setCategories(fetchedCategories);
        setEvents(eventDetails.events);
        setTotalBudget(eventDetails.events.reduce((sum, event) => sum + event.total_budget, 0)); // Sum up the budgets
      } catch (error) {
          console.error('Failed to fetch budget data', error);
      }
      };
  
      fetchData();
  }, [eventIds]);
  
  // Handle filtering of categories
  const filteredCategories = useMemo(() => {
    return categories.filter(category => {
      const categoryValue = category.category_name || '';
      const paidByValue = category.paid_by || '';

      const categoryMatch = !selectedCategoryFilter ||
        (selectedCategoryFilter === 'Uncategorized' && categoryValue.trim() === '') ||
        (capitalizeName(categoryValue) === selectedCategoryFilter);

      const paidByMatch = !selectedPaidByFilter ||
        (selectedPaidByFilter === 'Unassigned' && paidByValue.trim() === '') ||
        (capitalizeName(paidByValue) === selectedPaidByFilter);

      return categoryMatch && paidByMatch;
    });
  }, [categories, selectedCategoryFilter, selectedPaidByFilter]);

  // Calculate total expenses and paid so far for filtered categories
  useEffect(() => {
    setTotalExpense(filteredCategories.reduce((sum, category) => sum + category.expense, 0));
    setTotalPaidSoFar(filteredCategories.reduce((sum, category) => sum + (category.is_paid ? category.actual_paid : category.advance_paid), 0));
  }, [filteredCategories]);

  // Helper function to get unique values with proper formatting
  const getUniqueFilterValues = (key) => {
    const values = categories.map(category => {
      let value = category[key];
      
      // Handle empty/null values
      if (!value || value.trim() === '') {
        return key === 'category_name' ? 'Uncategorized' : 'Unassigned';
      }
      return value;
    });

    // Get unique values (case-insensitive) and sort them
    const uniqueValues = Array.from(new Set(
      values.map(value => value.toLowerCase())
    )).map(value => {
      if (value === 'uncategorized' || value === 'unassigned') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return capitalizeName(value);
    }).sort();

    return uniqueValues;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {events.map(event => <BudgetGuardrail key={event.id} label={event.title} limit={event.total_budget} expenses={categories.filter(category => category.event_id === event.id)} />)}
      {/* Total Budget Section */}
      <Card sx={{ 
        mt: { xs: 4, sm: 0 },
        mb: 4,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.2)'
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 30px rgba(0, 0, 0, 0.3)'
            : '0 8px 30px rgba(0, 0, 0, 0.15)',
        }
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AttachMoney sx={{ 
              fontSize: { xs: 24, sm: 32 }, 
              color: theme.palette.primary.main 
            }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Total Budget: ${totalBudget.toFixed(2)}
            </Typography>
          </Box>

          {/* Total Expense Progress Bar */}
          <Box sx={{ 
            mt: 3, 
            position: 'relative', 
            height: '8px', 
            borderRadius: '4px',
            overflow: 'hidden',
            bgcolor: theme.palette.grey[200]
          }}>
            <Box
              sx={{
                width: `${budgetPercentage(totalPaidSoFar, totalBudget)}%`,
                height: '100%',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px 0 0 4px',
                position: 'absolute',
              }}
            />
            <Box
              sx={{
                width: `${budgetPercentage(totalExpense - totalPaidSoFar, totalBudget)}%`,
                height: '100%',
                backgroundColor: theme.palette.warning.main,
                borderRadius: '0 4px 4px 0',
                position: 'absolute',
                left: `${budgetPercentage(totalPaidSoFar, totalBudget)}%`,
              }}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {selectedCategoryFilter || selectedPaidByFilter ? 'Filtered Expense' : 'Total Expense'}: ${totalExpense.toFixed(2)} | Paid So Far: ${totalPaidSoFar.toFixed(2)}
          </Typography>
        </CardContent>
      </Card>

      {/* Filter Section */}
      <Box sx={{ mb: 4 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                label="Category"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {getUniqueFilterValues('category_name').map((categoryName) => (
                  <MenuItem key={categoryName} value={categoryName}>{categoryName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Paid By</InputLabel>
              <Select
                value={selectedPaidByFilter}
                onChange={(e) => setSelectedPaidByFilter(e.target.value)}
                label="Paid By"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Paid By</MenuItem>
                {getUniqueFilterValues('paid_by').map((paidByName) => (
                  <MenuItem key={paidByName} value={paidByName}>{paidByName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      </Box>

      {/* Filtered Category List */}
      <Box sx={cardGridSx(230)}>
        {filteredCategories.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 3, sm: 4 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                  : '0 8px 30px rgba(0, 0, 0, 0.15)',
              }
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {selectedCategoryFilter || selectedPaidByFilter 
                  ? 'No expenses found for the selected filter'
                  : 'No expenses found for the selected events'}
              </Typography>
            </Card>
          </Box>
        ) : filteredCategories.map((category) => {
          const isExceeded = category.expected_paid < category.actual_paid;
          const displayCategory = !category.category_name || category.category_name.trim() === '' 
            ? 'Uncategorized' 
            : capitalizeName(category.category_name);
          const displayPaidBy = !category.paid_by || category.paid_by.trim() === ''
            ? 'Unassigned'
            : capitalizeName(category.paid_by);

          return (
            <Box key={category.id} sx={cardItemSx(280)}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: isExceeded ? '2px solid orange' : category.is_paid ? '2px solid green' : theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                    : '0 8px 30px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, minHeight: '1.5rem' }}>
                    {category.name}
                  </Typography>
                  
                  {/* Expense Details Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Expense: ${category.expense.toFixed(2)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Adv. Paid: ${category.advance_paid.toFixed(2)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Paid By: {displayPaidBy}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Category sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Category: {displayCategory}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Progress Section - Always at bottom */}
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={category.is_paid ? 100 : budgetPercentage(category.advance_paid, category.expense)}
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: category.is_paid ? theme.palette.success.main : theme.palette.primary.main,
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="body2" color={isExceeded ? "error" : category.is_paid ? "success" : "text.secondary"} sx={{ fontWeight: 500 }}>
                      {isExceeded ? `Exceeded by $${(category.actual_paid - category.expected_paid).toFixed(2)}` :
                       category.is_paid ? 'Fully Paid' : `${budgetPercentage(category.advance_paid, category.expense).toFixed(2)}% Paid`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <PaymentMilestones budget={category} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MultiViewBudgetTracking;
